/**
 * RoleDisplay Component
 *
 * A reusable component for displaying user role information with
 * clear visual indicators and permission descriptions.
 */

import { Info as InfoIcon } from '@mui/icons-material'
import { Box, Chip, Tooltip, Typography, useTheme, alpha } from '@mui/material'
import React from 'react'

import {
  getRoleCapabilityDescription,
  getRoleColor
} from '../../utils/rolePermissions'

export interface RoleDisplayProps {
  /** The user's role in the office */
  role: 'owner' | 'manager' | 'member'
  /** Size variant for the display */
  size?: 'small' | 'medium' | 'large'
  /** Whether to show the capability description */
  showDescription?: boolean
  /** Whether to show as a compact chip or expanded display */
  variant?: 'chip' | 'expanded'
  /** Additional styling */
  sx?: object
}

/**
 * RoleDisplay - Displays user role with clear visual indicators
 *
 * Shows the user's role with appropriate colors and optional descriptions
 * of what the role can do. Supports different sizes and variants for
 * various UI contexts.
 */
const RoleDisplay: React.FC<RoleDisplayProps> = ({
  role,
  size = 'medium',
  showDescription = false,
  variant = 'chip',
  sx = {}
}) => {
  const theme = useTheme()
  const roleColor = getRoleColor(role)
  const description = getRoleCapabilityDescription(role)
  const displayName = role.charAt(0).toUpperCase() + role.slice(1)

  // Chip variant - compact display
  if (variant === 'chip') {
    const chipSize = size === 'large' ? 'medium' : 'small'

    return (
      <Tooltip title={description} arrow>
        <Chip
          label={displayName}
          color={roleColor}
          size={chipSize}
          icon={<InfoIcon />}
          sx={{
            fontWeight: 'medium',
            ...sx
          }}
        />
      </Tooltip>
    )
  }

  // Expanded variant - full display with description
  const getTypographyVariant = () => {
    switch (size) {
      case 'small':
        return 'body2'
      case 'large':
        return 'h6'
      default:
        return 'body1'
    }
  }

  const getDescriptionVariant = () => {
    switch (size) {
      case 'small':
        return 'caption'
      case 'large':
        return 'body2'
      default:
        return 'body2'
    }
  }

  return (
    <Box
      sx={{
        p: size === 'large' ? 2 : 1.5,
        borderRadius: 2,
        backgroundColor:
          roleColor === 'default'
            ? alpha(theme.palette.grey[500], 0.1)
            : alpha(theme.palette[roleColor].main, 0.1),
        border:
          roleColor === 'default'
            ? `1px solid ${alpha(theme.palette.grey[500], 0.3)}`
            : `1px solid ${alpha(theme.palette[roleColor].main, 0.3)}`,
        ...sx
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        gap={1}
        mb={showDescription ? 1 : 0}
      >
        <Chip
          label={displayName}
          color={roleColor}
          size={size === 'large' ? 'medium' : 'small'}
          sx={{ fontWeight: 'medium' }}
        />
        <Typography
          variant={getTypographyVariant()}
          color={`${roleColor}.main`}
          sx={{ fontWeight: 'medium' }}
        >
          Role
        </Typography>
      </Box>

      {showDescription && (
        <Typography
          variant={getDescriptionVariant()}
          color='text.secondary'
          sx={{ mt: 0.5 }}
        >
          {description}
        </Typography>
      )}
    </Box>
  )
}

export default RoleDisplay
